import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, Check, CheckCheck, Mic, Image as ImageIcon, Heart, X, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import VoiceRecorder from "@/components/VoiceRecorder";
import OverflowMenu from "@/components/OverflowMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 2000;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPT_IMAGE = "image/jpeg,image/png,image/webp";
const POLL_MS = 3000;

function attachmentSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}
function fmtDuration(ms) {
  if (!ms || ms < 0) return "0:00";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function photoUrl(u) { if (!u) return null; return u.startsWith("http") ? u : `${BACKEND_URL}${u}`; }

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayLabel(dateStr, t) {
  try {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400000);
    if (isSameDay(d, today)) return t("app.messaging.today");
    if (isSameDay(d, yesterday)) return t("app.messaging.yesterday");
    return d.toLocaleDateString();
  } catch { return ""; }
}
function timeLabel(dateStr) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function VoicePlayer({ src, durationMs }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgressMs((a.currentTime || 0) * 1000);
    const onEnd = () => { setPlaying(false); setProgressMs(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, []);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;
  return (
    <div className="flex items-center gap-3 min-w-[200px]" data-testid="voice-player">
      <button
        type="button"
        onClick={toggle}
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
        data-testid="voice-play-toggle"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: "rgba(255,255,255,0.3)" }}
        >
          <div className="h-full" style={{ width: `${pct}%`, backgroundColor: "currentColor" }} />
        </div>
        <div className="text-[10px] mt-1 opacity-80" data-testid="voice-duration">
          {fmtDuration(durationMs)}
        </div>
      </div>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}

function MessageBubble({ msg, showAvatar, other, t, onToggleReaction, onOpenPhoto }) {
  const mine = msg.is_mine;
  const img = attachmentSrc(other?.main_photo_url);
  const hasHeart = msg.my_reaction === "heart";
  const totalHearts = (msg.reactions || []).filter((r) => r.emoji === "heart").length;
  const bubbleColor = mine ? "hsl(var(--nvl-accent))" : "hsl(var(--muted))";
  const textColor = mine ? "hsl(var(--nvl-bg))" : "hsl(var(--nvl-ink))";

  const renderBody = () => {
    if (msg.kind === "voice" && msg.attachment) {
      return (
        <div style={{ color: textColor }} data-testid="voice-bubble-body">
          <VoicePlayer
            src={attachmentSrc(msg.attachment.url)}
            durationMs={msg.attachment.duration_ms || 0}
          />
        </div>
      );
    }
    if (msg.kind === "photo" && msg.attachment) {
      const src = attachmentSrc(msg.attachment.url);
      return (
        <button
          type="button"
          onClick={() => onOpenPhoto && onOpenPhoto(src)}
          className="block overflow-hidden rounded-lg"
          data-testid={`photo-bubble-${msg.id}`}
        >
          <img
            src={src}
            alt={t("app.messaging.photo")}
            className="max-w-[240px] md:max-w-[300px] max-h-[360px] object-cover"
          />
        </button>
      );
    }
    return <span className="text-sm whitespace-pre-wrap break-words">{msg.body}</span>;
  };

  return (
    <div className={`group flex gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
      {!mine ? (
        <div
          className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-display text-sm"
          style={{
            backgroundImage: img ? `url(${img})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            backgroundColor: "hsl(var(--muted))",
            color: "hsl(var(--nvl-accent))",
            visibility: showAvatar ? "visible" : "hidden",
          }}
        >
          {img ? null : (other?.display_name || "?").charAt(0).toUpperCase()}
        </div>
      ) : null}
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col relative`}>
        <div className="relative">
          <div
            className={`${msg.kind === "photo" ? "p-0 overflow-hidden" : "px-4 py-2.5"} rounded-2xl text-sm`}
            style={{
              backgroundColor: msg.kind === "photo" ? "transparent" : bubbleColor,
              color: textColor,
              borderTopRightRadius: mine ? "6px" : undefined,
              borderTopLeftRadius: mine ? undefined : "6px",
            }}
          >
            {renderBody()}
          </div>

          {/* Heart reaction toggle — appears on hover for other user's message */}
          {!mine ? (
            <button
              type="button"
              onClick={() => onToggleReaction(msg)}
              data-testid={`react-heart-${msg.id}`}
              aria-label={hasHeart ? t("app.messaging.removeReaction") : t("app.messaging.reactHeart")}
              className={`absolute -bottom-2 -right-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full flex items-center justify-center border ${hasHeart ? "opacity-100" : "opacity-90"}`}
              style={{
                backgroundColor: "hsl(var(--nvl-bg))",
                borderColor: hasHeart ? "hsl(var(--nvl-accent))" : "hsl(var(--nvl-border))",
                color: hasHeart ? "hsl(var(--nvl-accent))" : "hsl(var(--nvl-ink-soft))",
              }}
            >
              <Heart size={13} fill={hasHeart ? "currentColor" : "none"} />
            </button>
          ) : null}

          {/* Reaction count badge (shared) */}
          {totalHearts > 0 ? (
            <span
              className="absolute -bottom-3 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full border"
              style={{
                backgroundColor: "hsl(var(--nvl-bg))",
                borderColor: "hsl(var(--nvl-border))",
                color: "hsl(var(--nvl-accent))",
              }}
              data-testid={`reaction-count-${msg.id}`}
            >
              <Heart size={9} fill="currentColor" />
              {totalHearts}
            </span>
          ) : null}
        </div>

        <div className={`flex items-center gap-1.5 ${totalHearts > 0 ? "mt-3" : "mt-1"} text-[10px]`} style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          <span>{timeLabel(msg.created_at)}</span>
          {mine ? (
            msg.read_at ? (
              <span className="inline-flex items-center gap-0.5" title={t("app.messaging.readAt")} data-testid={`read-receipt-double-${msg.id}`}>
                <CheckCheck size={11} style={{ color: "hsl(var(--nvl-accent))" }} />
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5" title={t("app.messaging.delivered")} data-testid={`read-receipt-single-${msg.id}`}>
                <Check size={11} />
              </span>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ChatRoom() {
  const { matchId } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null); // {file, previewUrl}
  const [openPhoto, setOpenPhoto] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastCountRef = useRef(0);

  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    });
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get(`/matches/${matchId}/messages`, { params: { limit: 100 } });
      setMessages(data.messages || []);
      setOther(data.other || null);
      // Mark received messages as read (auto)
      const anyUnreadReceived = (data.messages || []).some((m) => !m.is_mine && !m.read_at);
      if (anyUnreadReceived) {
        try { await api.patch(`/matches/${matchId}/messages/read`); } catch {}
      }
    } catch (e) {
      if (!silent) setError(extractError(e, t("app.messaging.loadingError")));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [matchId, t]);

  useEffect(() => {
    load();
    const id = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Autoscroll when message count grows
  useEffect(() => {
    if (messages.length !== lastCountRef.current) {
      const isFirstLoad = lastCountRef.current === 0;
      scrollToBottom(!isFirstLoad);
      lastCountRef.current = messages.length;
    }
  }, [messages, scrollToBottom]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft("");
    // Optimistic append with tempId
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body,
      is_mine: true,
      read_at: null,
      created_at: new Date().toISOString(),
      sender_id: "me",
      recipient_id: other?.user_id,
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const { data } = await api.post(`/matches/${matchId}/messages`, { body });
      // Replace optimistic with real
      setMessages((prev) => prev.map((m) => (m.id === tempId ? data : m)));
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      const status = e?.response?.status;
      if (status === 429) toast.error(t("app.messaging.rateLimited"));
      else toast.error(extractError(e, t("app.messaging.sendError")));
      setDraft(body); // restore
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const sendAttachment = async ({ kind, file, durationMs }) => {
    if (sending) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("kind", kind);
      if (durationMs) form.append("duration_ms", String(Math.floor(durationMs)));
      form.append("file", file, file.name || (kind === "voice" ? "voice.webm" : "photo.jpg"));
      const { data } = await api.post(`/matches/${matchId}/messages/attachment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((prev) => [...prev, data]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(t("app.messaging.rateLimited"));
      else if (detail === "file_too_large") toast.error(kind === "photo" ? t("app.messaging.photoTooLarge") : t("app.messaging.voiceTooLarge"));
      else if (detail === "unsupported_image_type" || detail === "unsupported_audio_type") toast.error(t("app.messaging.unsupportedType"));
      else toast.error(extractError(e, t("app.messaging.sendError")));
    } finally {
      setSending(false);
    }
  };

  const handlePhotoPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error(t("app.messaging.unsupportedType"));
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error(t("app.messaging.photoTooLarge"));
      return;
    }
    setPendingPhoto({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const sendPendingPhoto = async () => {
    if (!pendingPhoto) return;
    const { file, previewUrl } = pendingPhoto;
    setPendingPhoto(null);
    URL.revokeObjectURL(previewUrl);
    await sendAttachment({ kind: "photo", file });
  };

  const cancelPendingPhoto = () => {
    if (pendingPhoto?.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl);
    setPendingPhoto(null);
  };

  const onVoiceSend = async (blob, durationMs, mime) => {
    const ext = mime && mime.includes("mp4") ? "m4a" : (mime && mime.includes("ogg") ? "ogg" : "webm");
    const file = new File([blob], `voice.${ext}`, { type: mime || "audio/webm" });
    setRecorderOpen(false);
    await sendAttachment({ kind: "voice", file, durationMs });
  };

  const toggleReaction = async (msg) => {
    const had = msg.my_reaction === "heart";
    // Optimistic
    setMessages((prev) => prev.map((m) => {
      if (m.id !== msg.id) return m;
      const others = (m.reactions || []).filter((r) => r.user_id !== "me-local");
      const newReactions = had
        ? others.filter((r) => r.emoji !== "heart" || r.user_id !== msg.reactions.find((rr) => rr.emoji === "heart" && rr.user_id === (m.reactions.find((rr) => rr.emoji === "heart"))?.user_id)?.user_id)
        : [...(m.reactions || []), { user_id: "me-local", emoji: "heart", created_at: new Date().toISOString() }];
      return { ...m, my_reaction: had ? null : "heart", reactions: newReactions };
    }));
    try {
      if (had) {
        await api.delete(`/messages/${msg.id}/reactions`);
      } else {
        await api.post(`/messages/${msg.id}/reactions`, { emoji: "heart" });
      }
      // Poll will reconcile canonical reactions list
    } catch (e) {
      toast.error(extractError(e, t("app.messaging.sendError")));
      // Roll back
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, my_reaction: had ? "heart" : null, reactions: msg.reactions } : m));
    }
  };

  const grouped = useMemo(() => {
    // Insert day separators
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const d = new Date(m.created_at);
      const dayStr = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (dayStr !== lastDay) {
        out.push({ type: "separator", key: dayStr, label: dayLabel(m.created_at, t) });
        lastDay = dayStr;
      }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages, t]);

  const otherImg = photoUrl(other?.main_photo_url);
  const charsLeft = MAX_LEN - draft.length;

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-6rem)]" data-testid="chat-room">
      {/* Header */}
      <div
        className="flex items-center gap-3 pb-4 border-b flex-shrink-0"
        style={{ borderColor: "hsl(var(--nvl-border))" }}
      >
        <button
          onClick={() => navigate("/app/messages")}
          className="text-sm flex items-center"
          data-testid="chat-back"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <Link
          to={other ? `/app/discover/${other.user_id}` : "#"}
          className="flex items-center gap-3 flex-1 min-w-0"
          data-testid="chat-header-profile"
        >
          <div
            className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-display"
            style={{
              backgroundImage: otherImg ? `url(${otherImg})` : undefined,
              backgroundSize: "cover", backgroundPosition: "center",
              backgroundColor: "hsl(var(--muted))",
              color: "hsl(var(--nvl-accent))",
            }}
          >
            {otherImg ? null : (other?.display_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate" data-testid="chat-other-name">
              {other?.display_name || "..."}
            </p>
            <p className="text-[11px]" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.messaging.matched")}
            </p>
          </div>
        </Link>
        {other?.user_id ? (
          <OverflowMenu
            userId={other.user_id}
            contextMatchId={matchId}
            onBlocked={() => navigate("/app/messages")}
            testIdPrefix="chat-overflow"
          />
        ) : null}
      </div>

      {/* Message list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto py-6 space-y-2"
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="text-center text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.loading")}
          </div>
        ) : error ? (
          <div className="nvl-card p-4 text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="chat-error">
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12" data-testid="chat-empty">
            <p className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.messaging.inboxEmptyBody")}
            </p>
          </div>
        ) : (
          grouped.map((item, i) => {
            if (item.type === "separator") {
              return (
                <div key={item.key} className="flex justify-center my-2">
                  <span
                    className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{ backgroundColor: "hsl(var(--muted))", color: "hsl(var(--nvl-ink-soft))" }}
                  >
                    {item.label}
                  </span>
                </div>
              );
            }
            // Determine if the next msg is by the same sender (to hide avatar)
            const next = grouped[i + 1];
            const showAvatar = !next || next.type !== "msg" || next.msg.is_mine !== item.msg.is_mine;
            return (
              <MessageBubble
                key={item.key}
                msg={item.msg}
                showAvatar={showAvatar}
                other={other}
                t={t}
                onToggleReaction={toggleReaction}
                onOpenPhoto={setOpenPhoto}
              />
            );
          })
        )}
      </div>

      {/* Composer */}
      <div
        className="pt-3 border-t flex-shrink-0"
        style={{ borderColor: "hsl(var(--nvl-border))" }}
      >
        {recorderOpen ? (
          <VoiceRecorder
            busy={sending}
            onCancel={() => setRecorderOpen(false)}
            onSend={onVoiceSend}
          />
        ) : pendingPhoto ? (
          <div
            className="flex items-center gap-3 p-3 rounded-md border"
            style={{ borderColor: "hsl(var(--nvl-border))", backgroundColor: "hsl(var(--muted))" }}
            data-testid="photo-preview"
          >
            <img
              src={pendingPhoto.previewUrl}
              alt=""
              className="w-16 h-16 object-cover rounded-md flex-shrink-0"
            />
            <span className="flex-1 text-sm truncate">{t("app.messaging.previewPhoto")}</span>
            <button
              type="button"
              onClick={cancelPendingPhoto}
              className="nvl-btn nvl-btn-outline"
              data-testid="photo-preview-cancel"
              aria-label={t("app.messaging.cancel")}
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={sendPendingPhoto}
              disabled={sending}
              className="nvl-btn nvl-btn-primary"
              data-testid="photo-preview-send"
              aria-label={t("app.messaging.send")}
            >
              <Send size={14} />
            </button>
          </div>
        ) : (
          <div className="flex gap-2 items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_IMAGE}
              className="hidden"
              onChange={handlePhotoPick}
              data-testid="photo-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              className="w-11 h-11 rounded-md border flex items-center justify-center flex-shrink-0"
              style={{ borderColor: "hsl(var(--nvl-border))" }}
              data-testid="chat-photo-btn"
              aria-label={t("app.messaging.attachPhoto")}
            >
              <ImageIcon size={16} />
            </button>
            <button
              type="button"
              onClick={() => setRecorderOpen(true)}
              disabled={sending}
              className="w-11 h-11 rounded-md border flex items-center justify-center flex-shrink-0"
              style={{ borderColor: "hsl(var(--nvl-border))" }}
              data-testid="chat-voice-btn"
              aria-label={t("app.messaging.recordVoice")}
            >
              <Mic size={16} />
            </button>
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
              onKeyDown={onKeyDown}
              placeholder={t("app.messaging.placeholder")}
              rows={1}
              className="flex-1 nvl-input resize-none"
              style={{ minHeight: "44px", maxHeight: "160px" }}
            />
            <button
              onClick={send}
              disabled={sending || !draft.trim()}
              className="nvl-btn nvl-btn-primary"
              data-testid="chat-send"
              aria-label={t("app.messaging.send")}
            >
              <Send size={14} />
            </button>
          </div>
        )}
        {draft.length > MAX_LEN - 200 ? (
          <p className="mt-1 text-[10px] text-right" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.messaging.charsLeft", { n: charsLeft })}
          </p>
        ) : null}
      </div>

      {/* Full-screen photo lightbox */}
      {openPhoto ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,17,40,0.95)" }}
          onClick={() => setOpenPhoto(null)}
          data-testid="photo-lightbox"
        >
          <img src={openPhoto} alt="" className="max-w-full max-h-full object-contain rounded-md" />
          <button
            type="button"
            onClick={() => setOpenPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "hsl(var(--nvl-bg))", color: "hsl(var(--nvl-ink))" }}
            data-testid="photo-lightbox-close"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
